import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader.jsx';
import { Field } from '@/components/Field.jsx';
import { useAsignaciones } from './hooks/useAsignaciones.js';

const initialState = {
  chofer_id: '',
  vehiculo_id: '',
  fecha_desde: '',
  fecha_hasta: '',
  sucursal_id: '',
  observaciones: '',
};

export default function NuevaAsignacionFlota() {
  const navigate = useNavigate();
  const { loading, error, success, create, loadOpciones } = useAsignaciones();
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [opciones, setOpciones] = useState({ choferes: [], vehiculos: [] });

  const loadData = async () => {
    const data = await loadOpciones();
    setOpciones(data);
  };

  useEffect(() => {
    loadData();
  }, []);

  const choferesOptions = useMemo(
    () =>
      (opciones.choferes || []).map(chofer => ({
        value: String(chofer.id),
        label: [chofer.apellido, chofer.nombre].filter(Boolean).join(', ') || `Chofer ${chofer.id}`,
      })),
    [opciones.choferes]
  );

  const vehiculosOptions = useMemo(
    () =>
      (opciones.vehiculos || []).map(vehiculo => ({
        value: String(vehiculo.id),
        label: [vehiculo.patente, [vehiculo.marca, vehiculo.modelo].filter(Boolean).join(' ')].filter(Boolean).join(' · '),
      })),
    [opciones.vehiculos]
  );

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.chofer_id) nextErrors.chofer_id = 'Seleccioná un chofer.';
    if (!form.vehiculo_id) nextErrors.vehiculo_id = 'Seleccioná un vehículo.';
    if (!form.fecha_desde) nextErrors.fecha_desde = 'Ingresá la fecha desde.';
    if (form.fecha_hasta && form.fecha_hasta < form.fecha_desde) {
      nextErrors.fecha_hasta = 'La fecha hasta no puede ser anterior.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      chofer_id: Number(form.chofer_id),
      vehiculo_id: Number(form.vehiculo_id),
      fecha_desde: form.fecha_desde,
      fecha_hasta: form.fecha_hasta || null,
      sucursal_id: form.sucursal_id ? Number(form.sucursal_id) : null,
      observaciones: form.observaciones?.trim() || null,
    };

    const result = await create(payload);
    if (result) {
      setForm(initialState);
      setTimeout(() => navigate('/administracion/flota/asignaciones'), 600);
    }
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg),0.95)] pb-16">
      <div className="mx-auto max-w-[1200px] px-4 pb-10 pt-8">
        <PageHeader
          title="Nueva asignación de flota"
          subtitle="Seleccioná chofer y vehículo para iniciar la operación."
          breadcrumbs={[
            { label: 'Flota', href: '/administracion/flota' },
            { label: 'Asignaciones', href: '/administracion/flota/asignaciones' },
            { label: 'Nueva asignación' },
          ]}
        />

        <section className="mt-6 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.9)] p-6 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
          <form onSubmit={handleSubmit} className="grid grid-cols-12 gap-4">
            <Field label="Chofer" className="col-span-12 md:col-span-6" hint={errors.chofer_id}>
              <select
                name="chofer_id"
                value={form.chofer_id}
                onChange={handleChange}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Seleccioná un chofer activo</option>
                {choferesOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Vehículo" className="col-span-12 md:col-span-6" hint={errors.vehiculo_id}>
              <select
                name="vehiculo_id"
                value={form.vehiculo_id}
                onChange={handleChange}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Seleccioná un vehículo activo</option>
                {vehiculosOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Fecha desde" className="col-span-12 md:col-span-4" hint={errors.fecha_desde}>
              <input
                type="date"
                name="fecha_desde"
                value={form.fecha_desde}
                onChange={handleChange}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
            <Field label="Fecha hasta" className="col-span-12 md:col-span-4" hint={errors.fecha_hasta}>
              <input
                type="date"
                name="fecha_hasta"
                value={form.fecha_hasta}
                onChange={handleChange}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
            <Field label="Sucursal" className="col-span-12 md:col-span-4" hint="Opcional">
              <input
                type="number"
                name="sucursal_id"
                value={form.sucursal_id}
                onChange={handleChange}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                placeholder="ID de sucursal"
              />
            </Field>
            <Field label="Observaciones" className="col-span-12" hint="Opcional">
              <textarea
                name="observaciones"
                rows={4}
                value={form.observaciones}
                onChange={handleChange}
                className="mt-1 w-full resize-none rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                placeholder="Notas operativas para futuras integraciones con cargas y trackeo."
              />
            </Field>

            {error ? (
              <div className="col-span-12 rounded-2xl border border-[rgba(var(--danger,255_99_99),0.3)] bg-[rgba(var(--danger,255_99_99),0.1)] px-4 py-3 text-sm text-[rgb(var(--danger,255_99_99))]">
                {error}
              </div>
            ) : null}
            {success ? (
              <div className="col-span-12 rounded-2xl border border-[rgba(var(--brand),0.2)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--brand-700))]">
                {success}
              </div>
            ) : null}

            <div className="col-span-12 flex flex-wrap gap-3 justify-end">
              <button
                type="button"
                onClick={() => navigate('/administracion/flota/asignaciones')}
                className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
